import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ChevronLeftIcon, SearchIcon, UserCircleIcon, ArrowLeftIcon } from '@heroicons/react/outline';
import { findClientByDetails, createContract } from '../../services/newContractService';
import useAuthStore from '../../store/authStore';

const CreateContractPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const [step, setStep] = useState(1);

  // Step 1: Client Search
  const [nationalId, setNationalId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [client, setClient] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Step 2: Contract Details
  const [contractTitle, setContractTitle] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [contractDescription, setContractDescription] = useState('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSearchClient = async () => {
    if (!nationalId || !phoneNumber) return toast.error('الرجاء إدخال رقم الهوية ورقم الجوال للبحث');
    setIsSearching(true);
    try {
      const foundClient = await findClientByDetails(nationalId, phoneNumber);
      if (foundClient) {
        setClient(foundClient);
        toast.success('تم العثور على العميل. يمكنك المتابعة للخطوة التالية.');
      } else {
        setClient(null);
        toast.error('لم يتم العثور على عميل بهذه البيانات');
      }
    } catch (error) {
      console.error('Error searching for client:', error);
      toast.error('حدث خطأ أثناء البحث عن العميل');
    } finally {
      setIsSearching(false);
    }
  };

  const generatePaymentSchedule = (totalAmountValue: number) => {
    const numberOfPayments = Math.floor(Math.random() * 3) + 2; // 2, 3, or 4
    const paymentAmount = totalAmountValue / numberOfPayments;
    const schedule = [];
    const today = new Date();

    for (let i = 1; i <= numberOfPayments; i++) {
        const dueDate = new Date(today);
        dueDate.setMonth(today.getMonth() + i);
        schedule.push({
            amount: parseFloat(paymentAmount.toFixed(2)),
            dueDate: dueDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
            status: 'مستحق'
        });
    }
    return schedule;
  };

  const handleSubmit = async () => {
    if (!client) return toast.error('الرجاء البحث عن عميل وتحديده أولاً');
    if (!contractTitle || !totalAmount) return toast.error('الرجاء إدخال اسم العقد والمبلغ الإجمالي');
    
    setIsSubmitting(true);
    const totalAmountValue = parseFloat(totalAmount);
    const generatedPayments = generatePaymentSchedule(totalAmountValue);

    const contractData = {
      details: {
        title: contractTitle,
        description: contractDescription,
        totalAmount: totalAmountValue,
        clientId: client.id,
        clientName: client.fullName,
        lawyerId: userData?.uid,
        lawyerName: userData?.fullName,
      },
      payments: generatedPayments
    };

    try {
      await createContract(contractData);
      toast.success('تم تسجيل العقد بنجاح وهو الآن بانتظار موافقة العميل.');
      // Reset state and navigate
      setClient(null);
      setNationalId('');
      setPhoneNumber('');
      setContractTitle('');
      setContractDescription('');
      setTotalAmount('');
      setStep(1);
      navigate('/contracts');
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error('حدث خطأ أثناء تسجيل العقد');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="bg-white p-5 rounded-2xl shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">الخطوة ١: البحث عن العميل</h2>
            <div className="space-y-4">
              <input type="text" value={nationalId} onChange={(e) => setNationalId(e.target.value)} placeholder="رقم الهوية الوطنية للعميل" className="w-full p-3 bg-gray-50 rounded-lg" />
              <input type="text" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="رقم جوال العميل" className="w-full p-3 bg-gray-50 rounded-lg" />
            </div>
            <button 
              onClick={handleSearchClient} 
              disabled={isSearching} 
              style={{
                marginTop: '1rem',
                width: '100%',
                backgroundColor: 'oklch(0.55 0.24 262.91)',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                fontSize: '1rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: isSearching ? 0.7 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              <SearchIcon style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.5rem' }} />
              <span>{isSearching ? 'جاري البحث...' : 'بحث'}</span>
            </button>
            {client && (
              <div className="mt-4 p-4 bg-green-50 border-r-4 border-green-500 rounded-lg">
                <div className="flex items-center">
                  <UserCircleIcon className="w-12 h-12 text-gray-400 ml-4" />
                  <div>
                    <p className="font-semibold text-gray-800">{client.fullName}</p>
                    <p className="text-sm text-gray-500">{client.phone}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 2:
        return (
          <div className="bg-white p-5 rounded-2xl shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">الخطوة ٢: تفاصيل العقد</h2>
            <div className="space-y-4">
              <input type="text" value={contractTitle} onChange={(e) => setContractTitle(e.target.value)} placeholder="اسم العقد (مثال: استشارة قانونية)" className="w-full p-3 bg-gray-50 rounded-lg" />
              <input type="number" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} placeholder="المبلغ الإجمالي للعقد (ر.س)" className="w-full p-3 bg-gray-50 rounded-lg" />
              <textarea value={contractDescription} onChange={(e) => setContractDescription(e.target.value)} placeholder="وصف العقد" className="w-full p-3 bg-gray-50 rounded-lg" rows={3}></textarea>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen font-tajawal pb-24">
      <div className="flex items-center mb-6">
        <button onClick={() => step === 1 ? navigate(-1) : prevStep()} className="p-2 rounded-full hover:bg-gray-200">
          <ChevronLeftIcon className="h-5 w-5 text-gray-700 rotate-180" />
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mr-4">تسجيل عقد جديد</h2>
      </div>

      <div className="space-y-6">
        {renderStep()}

        <div className="flex items-center justify-between mt-6">
          <div style={{ display: 'flex', width: '100%', gap: '1rem' }}>
            {step > 1 && (
              <button 
                onClick={prevStep} 
                style={{
                  backgroundColor: '#e5e7eb',
                  color: '#1f2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  flex: '1 1 0%',
                  maxWidth: 'fit-content'
                }}
              >
                السابق
              </button>
            )}
            {step < 2 ? (
              <button 
                onClick={nextStep} 
                disabled={!client}
                style={{
                  backgroundColor: 'oklch(0.55 0.24 262.91)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  flex: '1 0 0%',
                  opacity: !client ? 0.5 : 1,
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>التالي</span>
                <ArrowLeftIcon style={{ width: '1.25rem', height: '1.25rem' }} />
              </button>
            ) : (
              <button 
                onClick={handleSubmit} 
                disabled={isSubmitting || !client || !totalAmount || !contractTitle}
                style={{
                  backgroundColor: '#198754',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  flex: '1 0 0%',
                  opacity: (isSubmitting || !client || !totalAmount || !contractTitle) ? 0.5 : 1,
                  marginLeft: 'auto',
                  transition: 'opacity 0.2s'
                }}
              >
                {isSubmitting ? 'جاري التسجيل...' : 'تسجيل العقد'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateContractPage;
